import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Zap, Calendar, ArrowRight, Star, Lock } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PLANS = [
  {
    id: 'weekly',
    name: 'Weekly Deal Boost',
    price: '$29',
    period: '/week',
    badge: null,
    description: 'Perfect for short campaigns, test promotions, or event weekends.',
    color: '#8A9A8B',
    features: [
      '1 active deal listing',
      '7-day full visibility',
      'Claim code tracking',
      'Map placement (Athens, GA)',
      'Category page placement',
      'Basic analytics dashboard',
    ],
    cta: 'Buy Weekly Plan',
    popular: false,
  },
  {
    id: 'campaign',
    name: 'Campaign Boost',
    price: '$49',
    period: '/2 weeks',
    badge: 'Best for Events',
    description: 'Maximum 2 deals. Ideal for launches, event nights, or holiday campaigns.',
    color: '#C8A97E',
    features: [
      'Up to 2 active deal listings',
      'Both deals promoted',
      '14-day full visibility',
      'Claim code tracking',
      'Campaign analytics',
      'Event targeting',
      'Featured placement',
    ],
    cta: 'Buy Campaign Plan',
    popular: true,
  },
  {
    id: 'monthly',
    name: 'Monthly Growth',
    price: '$79',
    period: '/month',
    badge: 'Most Deals',
    description: 'Maximum 4 deals. For businesses that want ongoing, full-platform visibility.',
    color: '#A07BC8',
    features: [
      'Up to 4 active deal listings (2 promoted + 2 standard)',
      '30-day full visibility',
      'Premium analytics dashboard',
      'Claim code tracking',
      'Customer behavior insights',
      'Deal performance reports',
      'Priority support',
    ],
    cta: 'Buy Monthly Plan',
    popular: false,
  },
];

const FAQS = [
  { q: 'Is the first month really free?', a: 'Yes! New businesses get 30 days free with 1 active deal — no credit card required. After 30 days, choose a paid plan to keep your listing active.' },
  { q: 'How do claim codes work?', a: 'When a student finds your deal on Perkly, they tap "Get My Claim Code" — a unique PKY-XXXXXX code is generated. They show it to you in person, and you verify it in your Business Dashboard. Each code is valid for 15 minutes and 1 use only.' },
  { q: 'What happens when my plan ends?', a: 'Your deals automatically deactivate when your subscription expires. Renew anytime to reactivate them — your deal content is saved.' },
  { q: 'Can I change my plan?', a: 'Yes. Upgrade, downgrade, or switch plans anytime. Your new plan activates immediately after payment.' },
  { q: 'What cities is Perkly in?', a: "We're live in Athens, GA and expanding to Atlanta, Savannah, and Nashville soon." },
];

export default function PricingPage() {
  const [activeFaq, setActiveFaq] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const handleBuyPlan = async (planId) => {
    if (!user) {
      login('/pricing');
      return;
    }
    setLoadingPlan(planId);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan_id: planId, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create checkout');
      window.location.href = data.url;
    } catch (err) {
      alert(err.message);
    }
    setLoadingPlan(null);
  };

  return (
    <div className="min-h-screen bg-[#F6F1E9]">
      <Navbar />

      {/* Hero */}
      <section className="py-20 px-4 text-center max-w-3xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A8B] mb-4">Business Plans</p>
        <h1
          className="text-5xl sm:text-6xl font-bold text-[#2B2B2B] leading-tight mb-5"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="pricing-title"
        >
          Simple Plans for Every Business
        </h1>
        <p className="text-lg text-[#2B2B2B]/65 mb-8 leading-relaxed">
          Reach thousands of students and locals in your city. Start free — no credit card needed.
        </p>
        <div className="inline-flex items-center gap-3 bg-[#C8A97E]/15 border border-[#C8A97E]/35 rounded-2xl px-6 py-4" data-testid="free-month-banner">
          <Star size={18} className="text-[#C8A97E]" fill="#C8A97E" />
          <div className="text-left">
            <p className="font-bold text-[#2B2B2B] text-sm">Try Perkly Free for Your First Month</p>
            <p className="text-xs text-[#2B2B2B]/60">1 active deal · No credit card · Cancel anytime</p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="px-4 pb-16 max-w-6xl mx-auto" data-testid="pricing-plans">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              data-testid={`plan-${plan.id}`}
              className={`relative bg-white rounded-3xl p-8 border transition-all duration-300 ${
                plan.popular
                  ? 'border-[#C8A97E] shadow-xl shadow-[#C8A97E]/10 scale-105'
                  : 'border-[#8A9A8B]/15 shadow-sm hover:shadow-md hover:-translate-y-1'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="text-xs font-bold px-4 py-1.5 rounded-full text-white" style={{ background: plan.color }}>
                    {plan.badge}
                  </span>
                </div>
              )}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-[#2B2B2B] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{plan.name}</h3>
                <p className="text-xs text-[#2B2B2B]/55 mb-4">{plan.description}</p>
                <div className="flex items-end gap-1">
                  <span className="text-4xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>{plan.price}</span>
                  <span className="text-sm text-[#2B2B2B]/55 mb-1">{plan.period}</span>
                </div>
              </div>
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5">
                    <Check size={15} className="mt-0.5 flex-shrink-0" style={{ color: plan.color }} />
                    <span className="text-sm text-[#2B2B2B]/75">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                data-testid={`plan-cta-${plan.id}`}
                onClick={() => handleBuyPlan(plan.id)}
                disabled={loadingPlan === plan.id}
                className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-full text-sm font-bold transition-all duration-300 disabled:opacity-60 ${
                  plan.popular
                    ? 'bg-[#2B2B2B] text-[#F6F1E9] hover:bg-[#C8A97E]'
                    : 'border-2 hover:text-[#F6F1E9] hover:bg-[#2B2B2B]'
                }`}
                style={plan.popular ? {} : { borderColor: plan.color, color: plan.color }}
              >
                {loadingPlan === plan.id ? (
                  <><div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> Processing...</>
                ) : (
                  <><Lock size={13} /> {plan.cta}</>
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Free trial note */}
        <div className="text-center mt-10 p-6 bg-white rounded-3xl border border-[#8A9A8B]/12 max-w-2xl mx-auto">
          <Zap size={24} className="text-[#C8A97E] mx-auto mb-3" />
          <h3 className="font-bold text-[#2B2B2B] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Get your first month free</h3>
          <p className="text-sm text-[#2B2B2B]/60 mb-4">
            Register your business and list one deal completely free for 30 days. No card needed.
          </p>
          <Link to="/business/register" className="btn-primary inline-flex text-sm">
            Start Free <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* How tracking works */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A8B] mb-3">Claim Code Tracking</p>
          <h2 className="text-3xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>Know Every Customer From Perkly</h2>
          <p className="text-[#2B2B2B]/60 mt-3 max-w-lg mx-auto text-sm">Every student who visits your business through Perkly uses a unique claim code — you verify it and see exactly who came from Perkly.</p>
        </div>
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { step: '01', title: 'Create your deal', desc: 'Set your deal details and publish it on Perkly.', color: '#C8A97E' },
            { step: '02', title: 'Student gets claim code', desc: 'Student taps "Get My Claim Code" — a unique PKY-XXXXXX code is generated instantly.', color: '#8A9A8B' },
            { step: '03', title: 'You verify & track', desc: 'Student shows code in-person. You verify it in your dashboard. Every redemption tracked.', color: '#7BA05B' },
          ].map((s) => (
            <div key={s.step} className="bg-[#F6F1E9] rounded-2xl p-6 text-center">
              <p className="text-3xl font-bold mb-3" style={{ color: s.color, fontFamily: 'Playfair Display, serif' }}>{s.step}</p>
              <h4 className="font-bold text-[#2B2B2B] mb-2">{s.title}</h4>
              <p className="text-xs text-[#2B2B2B]/60 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 max-w-3xl mx-auto" data-testid="pricing-faq">
        <h2 className="text-3xl font-bold text-center text-[#2B2B2B] mb-10" style={{ fontFamily: 'Playfair Display, serif' }}>Frequently Asked Questions</h2>
        <div className="space-y-3">
          {FAQS.map((faq, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#8A9A8B]/12 overflow-hidden">
              <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full flex items-center justify-between px-6 py-5 text-left" data-testid={`faq-toggle-${i}`}>
                <span className="font-semibold text-[#2B2B2B] text-sm">{faq.q}</span>
                <span className="text-[#C8A97E] text-lg font-bold ml-4">{activeFaq === i ? '−' : '+'}</span>
              </button>
              {activeFaq === i && (
                <div className="px-6 pb-5 text-sm text-[#2B2B2B]/65 leading-relaxed border-t border-[#8A9A8B]/10 pt-4">{faq.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4 bg-[#2B2B2B] text-center">
        <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Ready to Grow Your Business?</h2>
        <p className="text-white/60 mb-8 max-w-md mx-auto">Start with your free month and see how Perkly brings real customers through your door.</p>
        <Link to="/business/register" className="inline-flex items-center gap-2 bg-[#C8A97E] text-white px-8 py-4 rounded-full font-bold hover:bg-[#b8956b] transition-colors">
          List Your Deal Free <ArrowRight size={16} />
        </Link>
      </section>

      <Footer />
    </div>
  );
}
